import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Linkedin, Mail, Phone, MapPin, Send } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import TikTokIcon from "@/components/icons/TikTokIcon";

export default function Footer() {
  const { settings, menus } = useSettings();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/submissions", { form_type: "newsletter", name: email.split("@")[0], email });
      toast.success("Obrigado! Você receberá nossas novidades.");
      setEmail("");
    } catch {
      toast.error("Não foi possível cadastrar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[color:var(--marupiara-obsidian)] text-[color:var(--marupiara-ivory)] relative overflow-hidden">
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: "radial-gradient(ellipse at top, rgba(197,160,89,0.15), transparent 60%)" }} />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 relative">
        <div className="grid md:grid-cols-2 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
          {settings.logo_url ? (
            <img src={settings.logo_url} alt={settings.site_name || "Marupiara"} className="h-16 w-16 rounded-full mb-4" style={{ background: "#0A0A0A" }} />
          ) : null}
            <h4 className="font-serif text-2xl mb-3" style={{ color: "var(--marupiara-gold)" }}>
              {settings.site_name || "Marupiara Editoração"}
            </h4>
            <p className="text-sm text-[color:var(--marupiara-ivory)]/70 leading-relaxed max-w-sm">
              {settings.footer_description || "Onde histórias ganham vida."}
            </p>
            <div className="flex items-center gap-4 mt-6">
              {settings.instagram && (
                <a href={settings.instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="text-[color:var(--marupiara-ivory)]/70 hover:text-[color:var(--marupiara-gold)] transition-colors"><Instagram size={20} /></a>
              )}
              {settings.facebook && (
                <a href={settings.facebook} target="_blank" rel="noreferrer" data-testid="footer-facebook" aria-label="Facebook" className="text-[color:var(--marupiara-ivory)]/70 hover:text-[color:var(--marupiara-gold)] transition-colors"><Facebook size={20} /></a>
              )}
              {settings.youtube && (
                <a href={settings.youtube} target="_blank" rel="noreferrer" data-testid="footer-youtube" aria-label="YouTube" className="text-[color:var(--marupiara-ivory)]/70 hover:text-[color:var(--marupiara-gold)] transition-colors"><Youtube size={20} /></a>
              )}
              {settings.linkedin && (
                <a href={settings.linkedin} target="_blank" rel="noreferrer" data-testid="footer-linkedin" aria-label="LinkedIn" className="text-[color:var(--marupiara-ivory)]/70 hover:text-[color:var(--marupiara-gold)] transition-colors"><Linkedin size={20} /></a>
              )}
              {settings.tiktok && (
                <a href={settings.tiktok} target="_blank" rel="noreferrer" data-testid="footer-tiktok" aria-label="TikTok" className="text-[color:var(--marupiara-ivory)]/70 hover:text-[color:var(--marupiara-gold)] transition-colors"><TikTokIcon size={20} /></a>
              )}
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="overline text-[color:var(--marupiara-gold)] mb-4">Navegação</div>
            <ul className="space-y-3 text-sm">
              {menus.map((m) => (
                <li key={m.id}>
                  {m.external ? (
                    <a
                      href={m.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`footer-nav-${m.label.toLowerCase().replace(/\s/g, "-")}`}
                      className="hover:text-[color:var(--marupiara-gold-light)]"
                    >
                      {m.label}
                    </a>
                  ) : (
                    <Link
                      to={m.url}
                      data-testid={`footer-nav-${m.label.toLowerCase().replace(/\s/g, "-")}`}
                      className="hover:text-[color:var(--marupiara-gold-light)]"
                    >
                      {m.label}
                    </Link>
                  )}
                </li>
              ))}
              {menus.length === 0 && (
                <li className="text-[color:var(--marupiara-ivory)]/60">Nenhum menu publicado.</li>
              )}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="overline text-[color:var(--marupiara-gold)] mb-4">Contato</div>
            <ul className="space-y-3 text-sm text-[color:var(--marupiara-ivory)]/80">
              {settings.email && (
                <li className="flex items-start gap-2"><Mail size={16} className="mt-0.5 text-[color:var(--marupiara-gold)]" /><a href={`mailto:${settings.email}`}>{settings.email}</a></li>
              )}
              {settings.phone && (
                <li className="flex items-start gap-2"><Phone size={16} className="mt-0.5 text-[color:var(--marupiara-gold)]" />{settings.phone}</li>
              )}
              {settings.address && (
                <li className="flex items-start gap-2"><MapPin size={16} className="mt-0.5 text-[color:var(--marupiara-gold)]" />{settings.address}</li>
              )}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="overline text-[color:var(--marupiara-gold)] mb-4">Newsletter</div>
            <form onSubmit={onSubscribe} data-testid="newsletter-form" className="space-y-3">
              <input
                data-testid="newsletter-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 focus:border-[color:var(--marupiara-gold)] focus:outline-none placeholder:text-white/65"
              />
              <button
                data-testid="newsletter-submit"
                type="submit"
                disabled={loading}
                className="w-full btn-gold justify-center text-sm"
              >
                <Send size={14} /> {loading ? "Enviando…" : "Inscrever"}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-[color:var(--marupiara-gold)]/15 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-[color:var(--marupiara-ivory)]/50">
          <div>{settings.footer_copyright || "© Marupiara Editoração."}</div>
          <div className="flex items-center gap-4">
            <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-[color:var(--marupiara-gold)]">Área Administrativa</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
